import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, finalize } from 'rxjs';
import { WebhookDelivery } from '../../types/document-metadata';
import { DocumentTemplateService } from '../../services/document-template.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog.component';
import { webhookStatePresentation, webhookTargetSourceLabel, webhookTooltip } from '../../utils/webhook-delivery.utils';

/**
 * Status icon for the document's webhook, opening a popover with the delivery details and a manual
 * resend. The resend is offered directly only for a failed delivery; a delivered or still-retrying one
 * can be resent after confirmation, and a held or unconfigured document cannot be resent at all.
 */
@Component({
  selector: 'app-webhook-delivery',
  templateUrl: './webhook-delivery.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule, OverlayModule, A11yModule]
})
export class WebhookDeliveryComponent {
  readonly s3Key = input.required<string>();
  readonly delivery = input<WebhookDelivery | null | undefined>(null);

  /** Follows the input, but shows a resend's outcome immediately rather than waiting for the next document read. */
  readonly current = linkedSignal<WebhookDelivery | null>(() => this.delivery() ?? null);
  readonly sending = signal(false);
  readonly open = signal(false);

  private readonly documentService = inject(DocumentTemplateService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  readonly presentation = computed(() => webhookStatePresentation(this.current()?.state));
  readonly summary = computed(() => webhookTooltip(this.current()));
  readonly targetSourceLabel = computed(() => webhookTargetSourceLabel(this.current()?.targetSource));
  readonly lastAttemptAt = computed(() => {
    const value = this.current()?.lastAttemptAt;
    return value && !Number.isNaN(Date.parse(value)) ? value : null;
  });

  readonly canRetry = computed(() => this.current()?.state === 'failed');
  readonly canForceRetry = computed(() => {
    const state = this.current()?.state;
    return state === 'delivered' || state === 'retrying';
  });

  readonly popoverPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 }
  ];

  toggle(): void {
    this.open.update(isOpen => !isOpen);
  }

  close(): void {
    this.open.set(false);
  }

  retry(): void {
    if (!this.canRetry() || this.sending()) return;
    this.send();
  }

  forceRetry(): void {
    if (!this.canForceRetry() || this.sending()) return;
    const stillRetrying = this.current()?.state === 'retrying';
    this.close();
    const data: ConfirmDialogData = {
      title: 'Send the webhook anyway?',
      message: stillRetrying
        ? 'Automatic retries for this document are still running.'
        : 'The receiver already acknowledged this document.',
      consequences: [
        'The receiver gets the document’s current state again and may process it twice.',
        ...(stillRetrying ? ['A pending automatic retry can still send it once more afterwards.'] : [])
      ],
      confirmLabel: 'Override and resend'
    };
    this.dialog.open(ConfirmDialogComponent, { data, width: '440px' }).afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => this.send());
  }

  private send(): void {
    this.sending.set(true);
    this.documentService.retryWebhook(this.s3Key()).pipe(
      finalize(() => this.sending.set(false))
    ).subscribe({
      next: ({ webhookDelivery }) => {
        this.current.set(webhookDelivery);
        this.snackBar.open(
          webhookDelivery.state === 'delivered' ? 'Webhook delivered' : `Webhook failed: ${webhookDelivery.error ?? 'unknown error'}`,
          'Close',
          { duration: 4000 }
        );
      },
      error: (error: HttpErrorResponse) => {
        this.snackBar.open(error.error?.message ?? 'Could not send the webhook', 'Close', { duration: 5000 });
      }
    });
  }
}
